// src/pages/Candidati.jsx
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const STATI = ['In attesa','Colloquio fissato','In formazione','Collocato','Abbandonato / non risponde']
const STATI_COLOR = {
  'In attesa': { bg: '#FAEEDA', color: '#633806' },
  'Colloquio fissato': { bg: '#E6F1FB', color: '#0C447C' },
  'In formazione': { bg: '#EEEDFE', color: '#3C3489' },
  'Collocato': { bg: '#EAF3DE', color: '#27500A' },
  'Abbandonato / non risponde': { bg: '#FCEBEB', color: '#791F1F' },
}

export default function Candidati() {
  const { profile, can } = useAuth()
  const [candidati, setCandidati] = useState([])
  const [filtered, setFiltered] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [q, setQ] = useState('')
  const [filterStato, setFilterStato] = useState('')
  const [filterPercorso, setFilterPercorso] = useState('')
  const [filterReferente, setFilterReferente] = useState('')
  const [sortField, setSortField] = useState('cognome')
  const [sortDir, setSortDir] = useState('asc')
  const [selected, setSelected] = useState(null)
  const [colloqui, setColloqui] = useState([])
  const [apptFuturi, setApptFuturi] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState({})
  const [showColloquioModal, setShowColloquioModal] = useState(false)
  const [colloquioForm, setColloquioForm] = useState({ data: '', operatore_nome: '', note: '' })
  const [showImportModal, setShowImportModal] = useState(false)
  const [importPreview, setImportPreview] = useState([])
  const [importLoading, setImportLoading] = useState(false)
  const PAGE_SIZE = 30

  useEffect(() => { loadProfiles(); loadCandidati() }, [])

  useEffect(() => {
    const lower = q.toLowerCase()
    let f = candidati.filter(c =>
      (!lower || (c.nome + c.cognome + (c.cf||'') + (c.email||'')).toLowerCase().includes(lower)) &&
      (!filterStato || c.stato === filterStato) &&
      (!filterPercorso || (c.percorso||'').includes(filterPercorso)) &&
      (!filterReferente || c.referente_id === filterReferente)
    )
    // Ordinamento
    f = [...f].sort((a, b) => {
      let va, vb
      if (sortField === 'nome') { va = `${a.cognome} ${a.nome}`; vb = `${b.cognome} ${b.nome}` }
      else if (sortField === 'stato') { va = a.stato||''; vb = b.stato||'' }
      else if (sortField === 'percorso') { va = a.percorso||''; vb = b.percorso||'' }
      else if (sortField === 'referente') { va = a.profiles?`${a.profiles.cognome}`:''; vb = b.profiles?`${b.profiles.cognome}`:'' }
      else if (sortField === 'comune') { va = a.comune||''; vb = b.comune||'' }
      else { va = a[sortField]||''; vb = b[sortField]||'' }
      return sortDir === 'asc' ? va.localeCompare(vb,'it') : vb.localeCompare(va,'it')
    })
    setFiltered(f)
    setPage(0)
  }, [q, filterStato, filterPercorso, filterReferente, sortField, sortDir, candidati])

  async function loadProfiles() {
    const { data } = await supabase.from('profiles').select('id, nome, cognome').eq('attivo', true)
    setProfiles(data || [])
  }

  async function loadCandidati() {
    setLoading(true)
    let query = supabase
      .from('candidati')
      .select('*, profiles!candidati_referente_id_fkey(nome, cognome)')
      .order('cognome')

    if (!can.viewAll) query = query.eq('referente_id', profile.id)

    const { data } = await query
    setCandidati(data || [])
    setLoading(false)
  }

  async function openPanel(c) {
    setSelected(c)
    const today = new Date().toISOString().slice(0, 10)
    const [{ data: col }, { data: appts }] = await Promise.all([
      supabase.from('colloqui').select('*').eq('candidato_id', c.id).order('data', { ascending: false }),
      supabase.from('appuntamenti').select('*').eq('candidato_id', c.id).eq('stato', 'attivo').gte('data', today).order('data'),
    ])
    setColloqui(col || [])
    setApptFuturi(appts || [])
  }

  function openAdd() {
    setEditId(null)
    setForm({ stato: 'In attesa', referente_id: profile.id })
    setShowModal(true)
  }

  function openEdit(c) {
    setEditId(c.id)
    setForm({ nome: c.nome, cognome: c.cognome, cf: c.cf, email: c.email, tel: c.tel, comune: c.comune, stato: c.stato, percorso: c.percorso, referente_id: c.referente_id, note: c.note })
    setShowModal(true)
  }

  async function saveCandidate() {
    if (!form.nome || !form.cognome) return
    if (editId) {
      await supabase.from('candidati').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editId)
    } else {
      await supabase.from('candidati').insert([form])
    }
    setShowModal(false)
    loadCandidati()
    if (selected?.id === editId) openPanel({ ...selected, ...form })
  }

  async function saveColloquio() {
    if (!colloquioForm.data || !selected) return
    await supabase.from('colloqui').insert([{
      candidato_id: selected.id,
      data: colloquioForm.data,
      operatore_id: profile.id,
      operatore_nome: colloquioForm.operatore_nome || `${profile.nome} ${profile.cognome}`,
      note: colloquioForm.note,
    }])
    setShowColloquioModal(false)
    openPanel(selected)
  }

  async function exportCsv() {
    const bom = '\uFEFF'
    const header = 'Nome;Cognome;CF;Email;Telefono;Comune;Stato;Percorso'
    const rows = filtered.map(c =>
      [c.nome, c.cognome, c.cf, c.email, c.tel, c.comune, c.stato, c.percorso]
        .map(v => `"${(v||'').replace(/"/g,'""')}"`).join(';')
    )
    const blob = new Blob([bom + header + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'candidati.csv'; a.click()
  }

  function downloadTemplate() {
    const bom = '\uFEFF'
    const header = 'Nome;Cognome;CF;Email;Telefono;Comune;Stato;Percorso;Note'
    const examples = [
      'Mario;Rossi;RSSMRA80A01H501A;mario.rossi@email.it;3331234567;Roma;In attesa;Percorso 1;',
      'Giulia;Bianchi;BNCGLI90B41F205X;giulia.bianchi@email.it;3479876543;Milano;In formazione;Percorso 2;Note varie',
    ]
    const blob = new Blob([bom + header + '\n' + examples.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'template_candidati.csv'; a.click()
  }

  function handleImportFile(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = e => {
      const text = e.target.result.replace(/^\uFEFF/, '')
      const lines = text.split('\n').filter(l => l.trim())
      if (lines.length < 2) { alert('File vuoto o senza dati.'); return }
      const sep = lines[0].includes(';') ? ';' : ','
      const header = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/["\r]/g, ''))
      const rows = []
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(sep).map(v => v.trim().replace(/["\r]/g, ''))
        const obj = {}
        header.forEach((h, j) => obj[h] = vals[j] || '')
        const get = (...keys) => { for (const k of keys) { if (obj[k]) return obj[k] } return null }
        const nome = get('nome','name','firstname')
        const cognome = get('cognome','surname','lastname')
        if (!nome && !cognome) continue
        const stato = STATI.includes(get('stato','status')) ? get('stato','status') : 'In attesa'
        rows.push({
          nome: nome||'', cognome: cognome||'',
          cf: get('cf','codice fiscale','codicefiscale')||null,
          email: get('email')||null,
          tel: get('telefono','tel','phone')||null,
          comune: get('comune','city','città')||null,
          stato, percorso: get('percorso')||null,
          note: get('note','notes')||null,
          referente_id: profile.id,
        })
      }
      if (rows.length === 0) { alert('Nessun candidato valido. Controlla le colonne.'); return }
      setImportPreview(rows)
      setShowImportModal(true)
    }
    reader.readAsText(file, 'utf-8')
  }

  async function confirmImport() {
    if (!importPreview.length) return
    setImportLoading(true)
    const { error } = await supabase.from('candidati').insert(importPreview)
    setImportLoading(false)
    if (error) alert('Errore: ' + error.message)
    else {
      alert(`${importPreview.length} candidati importati!`)
      setShowImportModal(false)
      setImportPreview([])
      loadCandidati()
    }
  }

  function fmtDate(s) {
    if (!s) return '—'
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y}`
  }

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.topbar}>
        <h2 style={styles.title}>Candidati <span style={styles.count}>({candidati.length})</span></h2>
        <button style={styles.btnSecondary} onClick={downloadTemplate}>↓ Template CSV</button>
        <label style={{...styles.btnSecondary,cursor:'pointer'}}>
          ↑ Importa CSV
          <input type="file" accept=".csv,.txt" style={{display:'none'}} onChange={e=>handleImportFile(e.target.files[0])}/>
        </label>
        <button style={styles.btnSecondary} onClick={exportCsv}>↓ Esporta CSV</button>
        <button style={styles.btnPrimary} onClick={openAdd}>+ Nuovo candidato</button>
      </div>

      {/* Filtri */}
      <div style={styles.filters}>
        <input style={styles.search} placeholder="Cerca nome, CF, email..." value={q} onChange={e => setQ(e.target.value)} />
        <select style={styles.select} value={filterStato} onChange={e => setFilterStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s}>{s}</option>)}
        </select>
        <select style={styles.select} value={filterPercorso} onChange={e => setFilterPercorso(e.target.value)}>
          <option value="">Tutti i percorsi</option>
          {[...new Set(candidati.map(c=>c.percorso).filter(Boolean))].sort().map(p => <option key={p}>{p}</option>)}
        </select>
        <select style={styles.select} value={filterReferente} onChange={e => setFilterReferente(e.target.value)}>
          <option value="">Tutti i referenti</option>
          {profiles.map(p => <option key={p.id} value={p.id}>{p.nome} {p.cognome}</option>)}
        </select>
        {(filterStato||filterPercorso||filterReferente||q) &&
          <button style={{...styles.pageBtn,color:'#b91c1c',fontSize:12}} onClick={()=>{setQ('');setFilterStato('');setFilterPercorso('');setFilterReferente('')}}>✕ Azzera</button>
        }
      </div>

      {/* Tabella */}
      {loading
        ? <div style={styles.empty}>Caricamento...</div>
        : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}></th>
                  {[
                    {label:'Nome', field:'nome'},
                    {label:'Telefono', field:null},
                    {label:'Stato', field:'stato'},
                    {label:'Percorso', field:'percorso'},
                    {label:'Comune', field:'comune'},
                    {label:'Referente', field:'referente'},
                  ].map(({label,field}) => (
                    <th key={label} style={{...styles.th,cursor:field?'pointer':'default',userSelect:'none',whiteSpace:'nowrap'}}
                      onClick={()=>{
                        if (!field) return
                        if (sortField===field) setSortDir(d=>d==='asc'?'desc':'asc')
                        else { setSortField(field); setSortDir('asc') }
                      }}>
                      {label}
                      {field && <span style={{marginLeft:4,color:sortField===field?'#1a3a5c':'#ccc',fontSize:10}}>
                        {sortField===field?(sortDir==='asc'?'▲':'▼'):'⇅'}
                      </span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map(c => (
                  <tr key={c.id} style={styles.tr} onClick={() => openPanel(c)}>
                    <td style={styles.td}>
                      <div style={styles.avatar}>{c.nome[0]}{c.cognome[0]}</div>
                    </td>
                    <td style={styles.td}><strong>{c.nome} {c.cognome}</strong></td>
                    <td style={{ ...styles.td, color: '#888' }}>{c.tel || '—'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...STATI_COLOR[c.stato] }}>
                        {c.stato === 'Abbandonato / non risponde' ? 'Abbandonato' : c.stato}
                      </span>
                    </td>
                    <td style={{ ...styles.td, color: '#888', fontSize: 12 }}>
                      {c.percorso?.match(/\d+/)?.[0] ? `P${c.percorso.match(/\d+/)[0]}` : '—'}
                    </td>
                    <td style={{ ...styles.td, color: '#888' }}>{c.comune || '—'}</td>
                    <td style={{ ...styles.td, color: '#888', fontSize: 12 }}>
                      {c.profiles ? `${c.profiles.nome} ${c.profiles.cognome}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {/* Paginazione */}
      <div style={styles.pager}>
        <button style={styles.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
        <span style={{ fontSize: 13, color: '#888' }}>
          {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} di {filtered.length}
        </span>
        <button style={styles.pageBtn} disabled={(page + 1) * PAGE_SIZE >= filtered.length} onClick={() => setPage(p => p + 1)}>›</button>
      </div>

      {/* Detail Panel */}
      {selected && (
        <>
          <div style={styles.overlay} onClick={() => setSelected(null)} />
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <div style={styles.panelName}>{selected.nome} {selected.cognome}</div>
                <span style={{ ...styles.badge, ...STATI_COLOR[selected.stato] }}>{selected.stato}</span>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            <div style={styles.panelBody}>
              <div style={styles.dl}>Codice fiscale</div><div style={styles.dv}>{selected.cf || '—'}</div>
              <div style={styles.dl}>Email</div><div style={styles.dv}>{selected.email || '—'}</div>
              <div style={styles.dl}>Telefono</div><div style={styles.dv}>{selected.tel || '—'}</div>
              <div style={styles.dl}>Comune</div><div style={styles.dv}>{selected.comune || '—'}</div>
              <div style={styles.dl}>Percorso GOL</div><div style={styles.dv}>{selected.percorso || '—'}</div>
              {selected.note && <><div style={styles.dl}>Note</div><div style={styles.dv}>{selected.note}</div></>}

              <div style={styles.panelActions}>
                <button style={styles.btnSecondary} onClick={() => openEdit(selected)}>Modifica</button>
                <button style={styles.btnPrimary} onClick={() => { setShowColloquioModal(true); setColloquioForm({ data: new Date().toISOString().slice(0,10), operatore_nome: `${profile.nome} ${profile.cognome}`, note: '' }) }}>+ Colloquio</button>
              </div>

              <div style={styles.sectionTitle}>Prossimi appuntamenti ({apptFuturi.length})</div>
              {apptFuturi.length === 0
                ? <div style={styles.emptySmall}>Nessun appuntamento futuro.</div>
                : apptFuturi.map(a => (
                  <div key={a.id} style={styles.apptCard}>
                    <div style={styles.apptDate}>{fmtDate(a.data)} · {a.ora_inizio?.slice(0,5)}–{a.ora_fine?.slice(0,5)}</div>
                    <div style={styles.apptSala}>{a.sala}</div>
                  </div>
                ))
              }

              <div style={styles.sectionTitle}>Storico colloqui ({colloqui.length})</div>
              {colloqui.length === 0
                ? <div style={styles.emptySmall}>Nessun colloquio registrato.</div>
                : colloqui.map(c => (
                  <div key={c.id} style={styles.colloquioCard}>
                    <div style={styles.apptDate}>{fmtDate(c.data)} · {c.operatore_nome}</div>
                    <div style={{ fontSize: 13, color: '#333', marginTop: 3 }}>{c.note}</div>
                  </div>
                ))
              }
            </div>
          </div>
        </>
      )}

      {/* Modal aggiungi/modifica */}
      {showModal && (
        <>
          <div style={styles.overlay} onClick={() => setShowModal(false)} />
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>{editId ? 'Modifica candidato' : 'Nuovo candidato'}</h3>
            <div style={styles.grid2}>
              {[['Nome','nome'],['Cognome','cognome']].map(([label, key]) => (
                <div key={key} style={styles.field}>
                  <label style={styles.label}>{label}</label>
                  <input style={styles.input} value={form[key]||''} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} />
                </div>
              ))}
            </div>
            {[['Codice fiscale','cf'],['Email','email'],['Telefono','tel'],['Comune','comune'],['Percorso GOL','percorso']].map(([label, key]) => (
              <div key={key} style={styles.field}>
                <label style={styles.label}>{label}</label>
                <input style={styles.input} value={form[key]||''} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} />
              </div>
            ))}
            <div style={styles.field}>
              <label style={styles.label}>Stato</label>
              <select style={styles.input} value={form.stato||'In attesa'} onChange={e => setForm(f => ({...f, stato: e.target.value}))}>
                {STATI.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Referente</label>
              <select style={styles.input} value={form.referente_id||''} onChange={e => setForm(f => ({...f, referente_id: e.target.value}))}>
                <option value="">— nessuno —</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.nome} {p.cognome}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Note</label>
              <textarea style={{ ...styles.input, minHeight: 60 }} value={form.note||''} onChange={e => setForm(f => ({...f, note: e.target.value}))} />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>Annulla</button>
              <button style={styles.btnPrimary} onClick={saveCandidate}>Salva</button>
            </div>
          </div>
        </>
      )}

      {/* Modal colloquio */}
      {showColloquioModal && (
        <>
          <div style={styles.overlay} onClick={() => setShowColloquioModal(false)} />
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Aggiungi colloquio</h3>
            <div style={styles.field}><label style={styles.label}>Data</label>
              <input type="date" style={styles.input} value={colloquioForm.data} onChange={e => setColloquioForm(f => ({...f, data: e.target.value}))} />
            </div>
            <div style={styles.field}><label style={styles.label}>Operatore</label>
              <input style={styles.input} value={colloquioForm.operatore_nome} onChange={e => setColloquioForm(f => ({...f, operatore_nome: e.target.value}))} />
            </div>
            <div style={styles.field}><label style={styles.label}>Note</label>
              <textarea style={{ ...styles.input, minHeight: 70 }} value={colloquioForm.note} onChange={e => setColloquioForm(f => ({...f, note: e.target.value}))} />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowColloquioModal(false)}>Annulla</button>
              <button style={styles.btnPrimary} onClick={saveColloquio}>Salva</button>
            </div>
          </div>
        </>
      )}
      {/* Modal import */}
      {showImportModal && (
        <>
          <div style={styles.overlay} onClick={() => { setShowImportModal(false); setImportPreview([]) }} />
          <div style={{...styles.modal, width:'min(700px,96vw)'}}>
            <h3 style={styles.modalTitle}>Anteprima importazione — {importPreview.length} candidati</h3>
            <div style={{overflowY:'auto',maxHeight:360,border:'0.5px solid #e8e5e0',borderRadius:8}}>
              <table style={{...styles.table,fontSize:12}}>
                <thead><tr>
                  {['Nome','Cognome','CF','Email','Telefono','Comune','Stato','Percorso'].map(h=>(
                    <th key={h} style={{...styles.th,fontSize:11}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {importPreview.slice(0,20).map((r,i)=>(
                    <tr key={i}>
                      <td style={styles.td}>{r.nome}</td>
                      <td style={styles.td}>{r.cognome}</td>
                      <td style={{...styles.td,color:'#888'}}>{r.cf||'—'}</td>
                      <td style={{...styles.td,color:'#888'}}>{r.email||'—'}</td>
                      <td style={{...styles.td,color:'#888'}}>{r.tel||'—'}</td>
                      <td style={{...styles.td,color:'#888'}}>{r.comune||'—'}</td>
                      <td style={styles.td}><span style={{...styles.badge,...STATI_COLOR[r.stato]}}>{r.stato}</span></td>
                      <td style={{...styles.td,color:'#888'}}>{r.percorso||'—'}</td>
                    </tr>
                  ))}
                  {importPreview.length > 20 && (
                    <tr><td colSpan={8} style={{...styles.td,textAlign:'center',color:'#888',fontStyle:'italic'}}>...e altri {importPreview.length-20} candidati</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {importLoading && <div style={{fontSize:13,color:'#888',textAlign:'center'}}>Importazione in corso...</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={()=>{setShowImportModal(false);setImportPreview([])}}>Annulla</button>
              <button style={styles.btnPrimary} onClick={confirmImport} disabled={importLoading}>
                Importa {importPreview.length} candidati
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const styles = {
  wrap: { maxWidth: 1080, margin: '0 auto' },
  topbar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text)', flex: 1, margin: 0, letterSpacing: '-0.4px' },
  count: { fontWeight: 400, color: 'var(--text-muted)', fontSize: 16 },
  filters: { display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' },
  search: { flex: 1, minWidth: 200, padding: '9px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', outline: 'none', fontFamily: 'var(--font)', boxShadow: 'var(--shadow-sm)' },
  select: { padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', minWidth: 160, fontFamily: 'var(--font)', boxShadow: 'var(--shadow-sm)' },
  tableWrap: { overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, borderBottom: '1px solid var(--border)', background: 'var(--bg)', fontSize: 12 },
  tr: { cursor: 'pointer', transition: 'background .1s' },
  td: { padding: '10px 14px', borderBottom: '1px solid var(--border-light)', color: 'var(--text)', verticalAlign: 'middle' },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: 'var(--grad-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, boxShadow: '0 2px 8px rgba(99,102,241,0.3)' },
  badge: { display: 'inline-block', fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 500 },
  pager: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, justifyContent: 'center' },
  pageBtn: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '5px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font)', boxShadow: 'var(--shadow-sm)' },
  empty: { padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 },
  emptySmall: { fontSize: 13, color: 'var(--text-muted)', padding: '4px 0 8px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,14,23,0.5)', zIndex: 20, backdropFilter: 'blur(4px)' },
  panel: { position: 'fixed', top: 0, right: 0, height: '100%', width: 'min(420px, 98vw)', background: 'var(--surface)', borderLeft: '1px solid var(--border)', overflowY: 'auto', zIndex: 30, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: 0, boxShadow: 'var(--shadow-lg)' },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  panelName: { fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.3px' },
  closeBtn: { background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 8px', borderRadius: 8 },
  panelBody: { fontSize: 14 },
  panelActions: { display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' },
  dl: { fontSize: 11, color: 'var(--text-muted)', marginTop: 12, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 },
  dv: { fontSize: 14, color: 'var(--text)', wordBreak: 'break-word' },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', margin: '18px 0 10px', paddingTop: 16, borderTop: '1px solid var(--border-light)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  apptCard: { background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 8, border: '1px solid var(--border-light)' },
  colloquioCard: { background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 8, border: '1px solid var(--border-light)' },
  apptDate: { fontSize: 12, color: 'var(--text-muted)' },
  apptSala: { fontSize: 13, color: 'var(--text)', marginTop: 2 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1.75rem', zIndex: 40, width: 'min(500px, 96vw)', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: 'var(--shadow-lg)' },
  modalTitle: { fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '-0.3px' },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },
  input: { padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, background: 'var(--bg)', color: 'var(--text)', outline: 'none', width: '100%', fontFamily: 'var(--font)' },
  btnPrimary: { background: 'var(--grad-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' },
  btnSecondary: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' },
}
